/**
 * Client-side mirror of the backend's idle session timeout.
 *
 * The backend drops a session after N minutes without any server call (N =
 * `sessionIdleMinutes` from GET /api/auth/state). A user who is reading makes
 * no calls, so the shell:
 *   1. pings GET /api/me on real user activity once the last successful call
 *      is older than `pingAfterMs` (keep-alive);
 *   2. warns `warnBeforeMs` before the idle expiry when there was no activity;
 *   3. at expiry, confirms with one last ping (a 401 then sends the user to
 *      /login through the apiClient's global handler).
 *
 * Everything here is pure so the timing is unit-tested; `sessionMonitor` runs it against the clock.
 */

export interface SessionTimingConfig {
  idleMs: number;
  warnBeforeMs: number;
  pingAfterMs: number;
}

export type SessionPhase = "active" | "warning" | "expired";

export type PingResult = "alive" | "expired" | "unknown";

const MINUTE = 60_000;
/** The backend's default idle policy, used while the real one is unknown. */
const DEFAULT_IDLE_MINUTES = 30;
const MAX_WARNING_MS = 2 * MINUTE;
const MAX_PING_AFTER_MS = 4 * MINUTE;
const MIN_PING_AFTER_MS = 15_000;

/** Derives the timing from the idle policy; keeps sane ratios for short policies. */
export function timingForIdleMinutes(minutes: number | null | undefined): SessionTimingConfig {
  const idleMs = Math.max(1, Number.isFinite(minutes) && minutes ? minutes : DEFAULT_IDLE_MINUTES) * MINUTE;
  const warnBeforeMs = Math.min(MAX_WARNING_MS, Math.floor(idleMs / 3));
  const pingAfterMs = Math.min(MAX_PING_AFTER_MS, Math.max(MIN_PING_AFTER_MS, Math.floor((idleMs - warnBeforeMs) / 2)));
  return { idleMs, warnBeforeMs, pingAfterMs };
}

export function phaseAt(now: number, lastActivityAt: number, config: SessionTimingConfig): SessionPhase {
  const elapsed = now - lastActivityAt;
  if (elapsed >= config.idleMs) return "expired";
  if (elapsed >= config.idleMs - config.warnBeforeMs) return "warning";
  return "active";
}

export function expiresAt(lastActivityAt: number, config: SessionTimingConfig): number {
  return lastActivityAt + config.idleMs;
}

/** True when user activity should renew the session with a ping. */
export function shouldKeepAlive(now: number, lastActivityAt: number, config: SessionTimingConfig): boolean {
  return phaseAt(now, lastActivityAt, config) === "active" && now - lastActivityAt >= config.pingAfterMs;
}

export interface SessionState {
  phase: SessionPhase;
  expiresAt: number;
}
